import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'; // 👈 useSearchParams சேர்க்கப்பட்டுள்ளது
import { Plus, Search, Eye, Edit2, Filter, X, Briefcase, Trash2, CheckCircle2, Lock } from 'lucide-react';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';
import PageLoader from '../../helpers/PageLoader';
import SelectDropdown from '../../customStyles/SelectDropdown';
import ModernTable from '../ui/ModernTable';
import FormOffCanvas from '../ui/FormOffCanvas';
import DeleteConfirmationOffcanvas from '../../helpers/DeleteConfirmationOffcanvas';

// 👈 1. ஃபார்ம் மற்றும் விவரப் பக்கங்களை இங்கேயே இறக்குமதி செய்கிறோம்
import JobOpeningForm from './JobOpeningForm'; 
import JobOpeningDetail from './JobOpeningDetail';

const JobOpeningsList = () => {
  const { portalName } = useParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // 👈 2. KRA Library போல URL Parameters மூலம் வியூக்களைக் கட்டுப்படுத்துகிறோம் [1]
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("jobView") || "list"; // default is 'list'
  const selectedId = searchParams.get("id"); // edit/view செய்ய வேண்டிய ID

  const [selectedItem, setSelectedItem] = useState(null);
  
  // Filtering States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [divFilter, setDivFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dropdown lists
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Close Job Offcanvas State
  const [selectedJobToClose, setSelectedJobToClose] = useState(null);
  const [closeReason, setCloseReason] = useState('');
  const [isClosingAction, setIsClosingAction] = useState(false);

  // Delete Offcanvas State
  const [selectedJobToDelete, setSelectedJobToDelete] = useState(null);
  const [isDeletingAction, setIsDeletingAction] = useState(false);

  // Publish Offcanvas State
  const [selectedJobToPublish, setSelectedJobToPublish] = useState(null);
  const [isPublishingAction, setIsPublishingAction] = useState(false);

  const fetchFilters = useCallback(async () => {
    try {
      const [deptRes, divRes] = await Promise.all([
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url)
      ]);
      if (deptRes.data.success) setDepartments(deptRes.data.data || []);
      if (divRes.data.success) setDivisions(divRes.data.data || []);
    } catch (err) {
      console.error('Filter fetching error:', err);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllRecruitmentJobs.url);
      if (response.data.success) {
        setJobs(response.data.data || []);
      }
    } catch (err) {
      toast.error('Failed to load job openings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
    fetchJobs();
  }, [fetchFilters, fetchJobs]);

  // 👈 3. URL-ல் 'id' இருந்தால், அதற்குரிய வேலைவாய்ப்புத் தரவைத் தேடிச் சேமிக்கும் தானியங்கி அமைப்பு
  useEffect(() => {
    if (selectedId && jobs.length > 0) {
      const item = jobs.find(j => j._id === selectedId);
      if (item) {
        setSelectedItem(item);
      }
    } else if (!selectedId) {
      setSelectedItem(null);
    }
  }, [selectedId, jobs]);

  // 👈 4. URL-ன் வியூக்களை மாற்றும் செட்டப்
  const setView = useCallback((newView) => {
    const params = new URLSearchParams(searchParams);
    if (newView === "list") {
      params.delete("jobView");
      params.delete("id");
    } else {
      params.set("jobView", newView);
      if (!newView.startsWith("edit_") && !newView.startsWith("view_")) {
        params.delete("id");
      }
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSave = () => {
    setView("list");
    fetchJobs();
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesTitle = job.title?.toLowerCase().includes(term);
        const matchesJobId = job.jobId?.toLowerCase().includes(term);
        if (!matchesTitle && !matchesJobId) return false;
      }
      if (statusFilter && job.status !== statusFilter) return false;
      if (deptFilter && job.departmentId?._id !== deptFilter) return false;
      if (divFilter && job.divisionId?._id !== divFilter) return false;
      return true;
    });
  }, [jobs, searchTerm, statusFilter, deptFilter, divFilter]);

  const paginatedData = useMemo(() => {
    if (itemsPerPage === 'All') return filteredJobs;
    const start = (currentPage - 1) * Number(itemsPerPage);
    return filteredJobs.slice(start, start + Number(itemsPerPage));
  }, [filteredJobs, currentPage, itemsPerPage]);

  const handlePublishConfirm = async () => {
    if (!selectedJobToPublish) return;
    setIsPublishingAction(true);
    try {
      const response = await api.post(SummaryApi.publishJobOpening.url(selectedJobToPublish._id));
      if (response.data.success) {
        toast.success('Job published successfully');
        setSelectedJobToPublish(null);
        fetchJobs();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Publishing failed');
    } finally {
      setIsPublishingAction(false);
    }
  };

  const handleCloseJobSubmit = async (e) => {
    e.preventDefault();
    if (!closeReason.trim()) {
      toast.warning('Please enter a closing reason');
      return;
    }
    setIsClosingAction(true);
    try {
      const response = await api.post(SummaryApi.closeJobOpening.url(selectedJobToClose), { closeReason });
      if (response.data.success) {
        toast.success('Job closed successfully');
        setSelectedJobToClose(null);
        setCloseReason('');
        fetchJobs();
      }
    } catch (err) {
      toast.error('Failed to close job opening');
    } finally {
      setIsClosingAction(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedJobToDelete) return;
    setIsDeletingAction(true);
    try {
      const response = await api.delete(SummaryApi.deleteRecruitmentJob.url(selectedJobToDelete._id));
      if (response.data.success) {
        toast.success('Job opening deleted');
        setSelectedJobToDelete(null);
        fetchJobs();
      }
    } catch (err) {
      toast.error('Deletion failed');
    } finally {
      setIsDeletingAction(false);
    }
  };

  // 👈 5. எடிட் மற்றும் வியூ செய்யும்போது URL Parameters செட் செய்கிறோம்
  const handleEdit = (row) => {
    setSelectedItem(row);
    const params = new URLSearchParams(searchParams);
    params.set("jobView", "edit_job");
    params.set("id", row._id);
    setSearchParams(params, { replace: true });
  };

  const handleView = (row) => {
    setSelectedItem(row);
    const params = new URLSearchParams(searchParams);
    params.set("jobView", "view_job");
    params.set("id", row._id);
    setSearchParams(params, { replace: true });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-200',
      published: 'bg-green-100 text-green-700 border-green-200',
      on_hold: 'bg-amber-100 text-amber-700 border-amber-200',
      closed: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[status] || styles.draft}`}>
        {status.toUpperCase().replace('_', ' ')}
      </span>
    );
  };

  const columns = [
    {
      label: 'Job Details',
      key: 'title',
      render: (row) => (
        <div className="flex flex-col min-w-[200px]">
          <span className="font-semibold text-gray-950 text-sm leading-tight">{row.title}</span>
          <span className="text-[11px] text-gray-400 mt-0.5">{row.jobId || 'Draft ID'}</span>
        </div>
      )
    },
    {
      label: 'Dept / Div',
      key: 'departmentId',
      render: (row) => (
        <div className="text-xs">
          <div className="font-medium text-gray-800">{row.departmentId?.name || '-'}</div>
          <div className="text-gray-400 mt-0.5">{row.divisionId?.name || 'No Division'}</div>
        </div>
      )
    },
    {
      label: 'Openings',
      key: 'numberOfOpenings',
      render: (row) => (
        <span className="text-xs font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded">
          {row.numberOfHires} / {row.numberOfOpenings} Hired
        </span>
      )
    },
    {
      label: 'Mode',
      key: 'workMode',
      render: (row) => (
        <span className="capitalize text-xs font-semibold text-gray-600">
          {row.workMode} ({row.employmentType.replace('_', ' ')})
        </span>
      )
    },
    // 👈 Added Visibility column to handle isPublic dynamic badge render [4]
    {
      label: 'Visibility',
      key: 'isPublic',
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase ${
          row.isPublic 
            ? 'bg-blue-50 text-blue-700 border-blue-200' 
            : 'bg-purple-50 text-purple-700 border-purple-200'
        }`}>
          {row.isPublic ? 'Public' : 'Internal Only'}
        </span>
      )
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => getStatusBadge(row.status)
    }
  ];

  const tableActions = [
    { label: 'View Candidates & Details', icon: <Eye className="h-4 w-4" />, onClick: (row) => handleView(row) },
    { label: 'Edit Job Opening', icon: <Edit2 className="h-4 w-4" />, onClick: (row) => handleEdit(row) },
    {
      label: 'Publish Job',
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      show: (row) => row.status !== 'published' && row.status !== 'closed',
      onClick: (row) => setSelectedJobToPublish(row) 
    },
    {
      label: 'Close Job',
      icon: <Lock className="h-4 w-4 text-amber-600" />,
      show: (row) => row.status !== 'closed',
      onClick: (row) => setSelectedJobToClose(row._id)
    },
    {
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4 text-red-600" />,
      onClick: (row) => setSelectedJobToDelete(row) 
    }
  ];

  // 👈 6. வியூ கண்டிஷன்களைக் கையாளுதல் (Render checks for forms/detail views) [1]
  if (view === "create_job" || view === "edit_job") {
    return (
      <JobOpeningForm 
        job={selectedItem} 
        jobId={selectedId}
        onClose={() => setView("list")} 
        onSave={handleSave} 
      />
    );
  }

  if (view === "view_job") {
    return (
      <JobOpeningDetail 
        job={selectedItem} 
        jobId={selectedId}
        onClose={() => setView("list")} 
        onEdit={() => setView("edit_job")}
      />
    );
  }

  if (loading) return <PageLoader />;

  return (
   <>
    <div className="">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {/* 👈 மெயின் பட்டியலின் முகப்பில் இருக்கும்போது பேக் பட்டன் மெயின் டேபிற்கு அழைத்துச் செல்லும் */}
          <button 
            onClick={() => navigate(`/${portalName}/hr/recruitment-management?tab=jobs`)} 
            className="rounded-lg bg-red-50 border border-red-100 text-textPrimary hover:text-primary p-2 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Job Openings</h1>
        </div>
        
        <button 
          onClick={() => setView("create_job")} // 👈 create_job வியூவை அமைக்கிறது [1]
          className="flex items-center gap-2 bg-primary hover:bg-secondary text-sm text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all"
        >
          <Plus className="h-5 w-5" /> <span className="hidden sm:block">Create Job Opening</span>
        </button>
      </div>

      {/* Filters Area */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs by ID, Title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors ${showFilters ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-300'}`}>
            <Filter className="h-5 w-5" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SelectDropdown label="Status" options={[{ label: 'All', value: '' }, { label: 'Draft', value: 'draft' }, { label: 'Published', value: 'published' }, { label: 'On Hold', value: 'on_hold' }, { label: 'Closed', value: 'closed' }]} value={statusFilter} onChange={setStatusFilter} valueKey="value" labelKey="label" />
            <SelectDropdown label="Department" options={[{ name: 'All Departments', _id: '' }, ...departments]} value={deptFilter} onChange={setDeptFilter} valueKey="_id" labelKey="name" searchable />
            <SelectDropdown label="Division" options={[{ name: 'All Divisions', _id: '' }, ...divisions]} value={divFilter} onChange={setDivFilter} valueKey="_id" labelKey="name" searchable />
            <button onClick={() => { setStatusFilter(''); setDeptFilter(''); setDivFilter(''); setSearchTerm(''); }} className="text-red-600 text-sm font-medium flex items-center gap-1 hover:underline self-end pb-3">
              <X className="h-4 w-4" /> Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Modern Table */}
      {filteredJobs.length > 0 && (
        <ModernTable
          columns={columns}
          data={paginatedData}
          actions={tableActions}
          selectable={false}
          pagination={{
            totalItems: filteredJobs.length,
            itemsPerPage: itemsPerPage,
            currentPage: currentPage,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: (v) => { setItemsPerPage(v); setCurrentPage(1); }
          }}
        />
      )}

      {/* Empty State */}
      {!loading && filteredJobs.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center mt-4">
          <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No Job Openings found</h3>
          <p className="text-gray-500">Try modifying filters or add a new job opening.</p>
        </div>
      )}
    </div>
  
   
      {/* Close Job Off-canvas drawer */}
      <FormOffCanvas isOpen={!!selectedJobToClose} onClose={() => setSelectedJobToClose(null)} title="Close Job Opening">
        <form onSubmit={handleCloseJobSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">Please provide a reason to close this job opening. Candidates applying will no longer see this vacancy.</p>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Reason for Closing *</label>
            <textarea
              required
              rows={4}
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-red-500"
              placeholder="e.g. Budget constraints, positions filled internally..."
            />
          </div>
          <button
            type="submit"
            disabled={isClosingAction}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold transition-all shadow disabled:opacity-50"
          >
            {isClosingAction ? 'Closing...' : 'Close Opening'}
          </button>
        </form>
      </FormOffCanvas>

      {/* Delete Confirmation Offcanvas */}
      <DeleteConfirmationOffcanvas
        isOpen={!!selectedJobToDelete}
        onClose={() => setSelectedJobToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Job Opening"
        itemName={selectedJobToDelete?.title}
        isLoading={isDeletingAction}
        deleteType="hard"
      />

      {/* Publish Confirmation Offcanvas */}
      <DeleteConfirmationOffcanvas
        isOpen={!!selectedJobToPublish}
        onClose={() => setSelectedJobToPublish(null)}
        onConfirm={handlePublishConfirm}
        title="Publish Job Opening"
        itemName={selectedJobToPublish?.title}
        isLoading={isPublishingAction}
        confirmButtonText="Publish"
        customDescription={
          <span>
            Are you sure you want to publish <span className="font-semibold text-gray-800">{selectedJobToPublish?.title}</span>? Once published, this job opening will be visible on the public careers page.
          </span>
        }
      /> 
    </>
  );
};

export default JobOpeningsList;